import React, { useState } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Mail, Lock, AlertTriangle, ArrowLeft, LogIn } from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { MFAVerification } from '../../components/admin/MFAVerification';

export const AdminAuth: React.FC = () => {
  const { signIn, error } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      // First, verify admin credentials
      await signIn(formData.email, formData.password);
      
      // Store admin email for MFA
      setAdminEmail(formData.email);
      
      // Show MFA verification step
      setShowMFA(true);
    } catch (err) {
      // Error is handled by useAuth hook
    } finally {
      setLoading(false);
    }
  };

  const handleMFASuccess = () => {
    // MFA verification successful, proceed to admin dashboard
    navigate('/admin');
  };

  const handleMFABack = () => {
    // Clear MFA session and go back to login form
    sessionStorage.removeItem('admin_mfa_verified');
    sessionStorage.removeItem('admin_mfa_timestamp');
    setShowMFA(false);
    setAdminEmail('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Show MFA verification if credentials are valid
  if (showMFA) {
    return (
      <MFAVerification
        email={adminEmail}
        onVerificationSuccess={handleMFASuccess}
        onBack={handleMFABack}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center text-gray-400 hover:text-gray-300 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Main Site
          </Link>
          
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 flex items-center justify-center">
              <img 
                src="/icons/ggg-logo-512.png" 
                alt="GGG Logo" 
                className="w-16 h-16 object-contain"
              />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white">
            Admin Access
          </h2>
          <p className="mt-2 text-gray-400">
            Authorized personnel only
          </p>
        </div>

        {/* Warning */}
        <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-yellow-200">
                Restricted Access with MFA
              </h3>
              <p className="text-sm text-yellow-300 mt-1">
                This area requires multi-factor authentication. All access attempts are logged and monitored.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Administrator Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-white placeholder-gray-400"
                  placeholder="Enter your admin email"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-white placeholder-gray-400"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <LoadingSpinner size="sm" color="white" />
              ) : (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Access Admin Dashboard
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>Need admin access? Contact your system administrator.</p>
          <p className="mt-2">
            <Link to="/auth" className="text-red-400 hover:text-red-300">
              Regular user login →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
